import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';
import { DEFAULT_API_BASE, STORAGE_KEYS } from '../config';

const ConfigContext = createContext(null);

const restaurantKey = (companyId) =>
  companyId ? `${STORAGE_KEYS.restaurantId}:${companyId}` : STORAGE_KEYS.restaurantId;

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const normalizeStorageValue = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const persistCompanyValue = (companyId) => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    if (companyId) {
      storage.setItem(STORAGE_KEYS.companyId, companyId);
    } else {
      storage.removeItem(STORAGE_KEYS.companyId);
    }
  } catch {
    // Scope persistence is best-effort and must not break authenticated navigation.
  }
};

const persistRestaurantValue = (companyId, restaurantId) => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  const key = restaurantKey(companyId);

  try {
    if (restaurantId) {
      storage.setItem(key, restaurantId);
      storage.setItem(STORAGE_KEYS.restaurantId, restaurantId);
    } else {
      storage.removeItem(key);
      storage.removeItem(STORAGE_KEYS.restaurantId);
    }
  } catch {
    // Scope persistence is best-effort and must not break authenticated navigation.
  }
};

const loadStored = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return {
      companyId: null,
      restaurantId: null
    };
  }

  let companyId = null;
  let restaurantId = null;

  try {
    const storedCompany = storage.getItem(STORAGE_KEYS.companyId);
    companyId = storedCompany && storedCompany.length ? storedCompany : null;

    // Prefer company-scoped value; keep legacy global key only as a one-time fallback.
    const scopedRestaurant = storage.getItem(restaurantKey(companyId));
    const legacyRestaurant = storage.getItem(STORAGE_KEYS.restaurantId);
    restaurantId = (scopedRestaurant && scopedRestaurant.length
      ? scopedRestaurant
      : legacyRestaurant && legacyRestaurant.length
        ? legacyRestaurant
        : null);
  } catch {
    return {
      companyId: null,
      restaurantId: null
    };
  }

  return { companyId, restaurantId };
};

export const ConfigProvider = ({ children }) => {
  const initial = loadStored();
  const [companyId, setCompanyIdState] = useState(initial.companyId);
  const [restaurantId, setRestaurantIdState] = useState(initial.restaurantId);

  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  const persistRestaurant = useCallback((value, targetCompanyId) => {
    const storage = getLocalStorage();
    if (!storage) {
      return;
    }

    const key = restaurantKey(targetCompanyId);

    try {
      if (value) {
        storage.setItem(key, value);
      } else {
        storage.removeItem(key);
      }

      // Legacy compatibility key; no longer used for cross-company reads.
      if (value) {
        storage.setItem(STORAGE_KEYS.restaurantId, value);
      } else {
        storage.removeItem(STORAGE_KEYS.restaurantId);
      }
    } catch {
      // Scope persistence is best-effort and must not break authenticated navigation.
    }
  }, []);

  const loadRestaurant = useCallback((targetCompanyId) => {
    const storage = getLocalStorage();
    if (!storage) {
      return null;
    }

    const key = restaurantKey(targetCompanyId);

    try {
      const stored = storage.getItem(key);
      return stored && stored.length ? stored : null;
    } catch {
      return null;
    }
  }, []);

  const setCompanyId = useCallback((value) => {
    const normalized = normalizeStorageValue(value);
    setCompanyIdState(normalized);
    persistCompanyValue(normalized);

    const nextRestaurant = loadRestaurant(normalized);
    setRestaurantIdState(nextRestaurant);
  }, [loadRestaurant]);

  const setRestaurantId = useCallback((value) => {
    const normalized = normalizeStorageValue(value);
    setRestaurantIdState(normalized);
    persistRestaurant(normalized, companyIdRef.current);
  }, [persistRestaurant]);

  const setOperationalScope = useCallback((nextCompanyId, nextRestaurantId) => {
    const normalizedCompanyId = normalizeStorageValue(nextCompanyId);
    const normalizedRestaurantId = normalizeStorageValue(nextRestaurantId);

    setCompanyIdState(normalizedCompanyId);
    setRestaurantIdState(normalizedRestaurantId);
    persistCompanyValue(normalizedCompanyId);
    persistRestaurantValue(normalizedCompanyId, normalizedRestaurantId);
  }, []);

  const resetRestaurant = useCallback(() => {
    setRestaurantIdState(null);
    persistRestaurant(null, companyIdRef.current);
  }, [persistRestaurant]);

  const value = useMemo(
    () => ({
      apiBase: DEFAULT_API_BASE,
      setApiBase: () => {}, // No-op since apiBase is now constant
      companyId,
      restaurantId,
      setCompanyId,
      setRestaurantId,
      setOperationalScope,
      resetRestaurant
    }),
    [companyId, restaurantId, setCompanyId, setRestaurantId, setOperationalScope, resetRestaurant]
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return ctx;
};
